import React from 'react';
import { Link } from 'react-router-dom';
import { Project, OrganizationQuotaDetails, Organization } from '../../../types/domain';
import { OrganizationMeters } from '../components/OrganizationMeters';
import { ProjectCard } from '../components/ProjectCard';
import { InnerLayoutSection } from '../../../components/layout/InnerLayout';
import {
  Plus,
  ArrowRight,
  FolderKanban,
} from 'lucide-react';

interface OrganizationOverviewTabProps {
  quota: OrganizationQuotaDetails | null;
  currentOrg: Organization | null;
  projects: Project[];
  membersCount: number;
  canManageOrg: boolean;
  onCreateProjectClick: () => void;
}

export const OrganizationOverviewTab: React.FC<OrganizationOverviewTabProps> = ({
  quota,
  currentOrg,
  projects,
  membersCount,
  canManageOrg,
  onCreateProjectClick,
}) => {
  return (
    <div className="space-y-6">
      {/* Resource Quota Metrics */}
      <OrganizationMeters
        quota={quota}
        currentOrg={currentOrg}
        membersCount={membersCount}
        cardBgClass="bg-cf-surface"
      />

      {/* Projects in this Organization */}
      <InnerLayoutSection
        title={`Projects in this organization (${projects.length})`}
        actions={
          canManageOrg
            ? [
                {
                  label: 'New Project',
                  icon: Plus,
                  variant: 'primary',
                  onClick: onCreateProjectClick,
                },
              ]
            : undefined
        }
      >

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {projects.slice(0, 7).map((p) => (
            <ProjectCard key={p.id} project={p} orgId={currentOrg?.id} />
          ))}

          {projects.length > 7 && (
            <Link
              to={`/organization/${currentOrg?.id}/projects`}
              className="p-4 rounded-xl bg-cf-surface/60 hover:bg-cf-surface border border-dashed border-cf-border hover:border-cf-border/80 transition-all flex flex-col items-center justify-center text-center cursor-pointer group select-none min-h-[85px]"
            >
              <div className="flex items-center gap-1.5 text-xs font-semibold text-cf-textPrimary group-hover:text-cf-orange transition-colors">
                <span>View all {projects.length} projects</span>
                <ArrowRight size={13} className="group-hover:translate-x-0.5 transition-transform" />
              </div>
              <span className="text-xs text-cf-textMuted mt-1">
                +{projects.length - 7} more projects
              </span>
            </Link>
          )}

          {projects.length === 0 && (
            <div className="col-span-full py-12 px-4 text-center rounded-xl bg-cf-surface border border-cf-border">
              <div className="w-10 h-10 rounded-full bg-cf-bg border border-cf-border flex items-center justify-center mx-auto text-cf-textSecondary mb-2">
                <FolderKanban size={20} />
              </div>
              <div className="text-xs font-semibold text-cf-textPrimary uppercase tracking-wider">
                No Projects Yet
              </div>
              <p className="text-xs text-cf-textSecondary mt-1 max-w-sm mx-auto">
                Create your first project to start organizing datastores and endpoints.
              </p>
            </div>
          )}
        </div>
      </InnerLayoutSection>
    </div>
  );
};
